import { useCallback } from 'react';

import { useLoggedInEmployeeId } from '~/components/Auth';
import { useAllEmployees } from '~/data/employees';
import { OrganizationUnit, useMyUnits } from '~/data/orgunits';
import { getMgrEmpRelationship } from '~/data/search';

type UseHasAccessOverEmployeeOutput = {
  hasFullAccessOverEmp: (
    _empId: number,
    unitId: number,
  ) => OrganizationUnit | undefined;
  hasFullAccessOverEmployeesInUnit: (unitId?: number) => boolean;
  hasFullAccessOverEmployee: (employeeId?: number) => boolean;
  isMgrOrHigherMgr: (targetId: number) => Promise<boolean>;
  isLoading: boolean;
};

export const useHasAccessOverEmployee = (): UseHasAccessOverEmployeeOutput => {
  const {
    myUnits,
    isLoading: isLoadingMyUnits,
    hasData: hasDataMyUnits,
  } = useMyUnits(true);
  const {
    allEmployees,
    isLoading: isLoadingAllEmployees,
    hasData: hasDataAlleEmployees,
  } = useAllEmployees();
  const isLoading = isLoadingMyUnits || isLoadingAllEmployees;
  const hasData = hasDataMyUnits && hasDataAlleEmployees;
  const loggedInEmpId = useLoggedInEmployeeId();

  // TODO: Update usage cases to the other functions
  const hasFullAccessOverEmp = useCallback(
    (_empId: number, unitId: number) => {
      return myUnits.find((unit) => unit.fOrgUnitId === unitId);
    },
    [myUnits],
  );

  const isMgrOrHigherMgr = useCallback(
    async (targetId: number) => {
      const rel = await getMgrEmpRelationship(loggedInEmpId, targetId);
      return rel === 'DIRECT' || rel === 'INDIRECT';
    },
    [loggedInEmpId],
  );

  const hasFullAccessOverEmployeesInUnit = useCallback(
    (unitId?: number): boolean => {
      return !!unitId && !!myUnits.length
        ? !!myUnits.find((unit) => unit.fOrgUnitId === unitId)
        : false;
    },
    [myUnits],
  );

  const hasFullAccessOverEmployee = useCallback(
    (employeeId?: number) => {
      if (
        typeof employeeId === 'undefined' ||
        !Object.keys(allEmployees).length
      ) {
        return false;
      }

      if (employeeId === loggedInEmpId) {
        return true;
      }

      const unitId = allEmployees[employeeId]?.fEmpUnit;

      return !!unitId ? hasFullAccessOverEmployeesInUnit(unitId) : false;
    },
    [hasFullAccessOverEmployeesInUnit, allEmployees, loggedInEmpId],
  );

  return {
    hasFullAccessOverEmp,
    hasFullAccessOverEmployeesInUnit,
    hasFullAccessOverEmployee,
    isMgrOrHigherMgr,
    isLoading: isLoading && !hasData,
  };
};
